import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { GoogleLogin } from '@react-oauth/google';
import {
  useGetInvitationByTokenQuery,
  useGetAuthConfigQuery,
  useAcceptInvitationMutation,
  type TokenData,
} from '@/redux/internal-services/onboardingApi';
import { setSession, clearSession } from '@/redux/slices/sessionStateSlice';
import { getErrorDetail } from '@/utils/apiError';
import LeftSidePanel from '@/components/UI/LeftSidePanel';
import OnboardingStepNav from '@/components/Onboarding/OnboardingStepNav';
import { Button } from '@/components/UI/Button';

// ── Icons ─────────────────────────────────────────────────────────────────────

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const ProfileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);


const EyeIcon = ({ off }: { off?: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    {off ? (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    ) : (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

// ── Step definitions (shown in left nav to set expectations) ──────────────────

const NAV_STEPS = [
  { id: 'password', label: 'Set Password',   icon: <LockIcon /> },
  { id: 'done',     label: 'Go to Dashboard', icon: <ProfileIcon /> },
];

// ── Input styles (consistent with ProfileStep) ────────────────────────────────

const inputCls =
  'w-full border border-ui-border rounded-lg px-3 py-2.5 text-sm bg-ui-input ' +
  'focus:outline-none focus:ring-2 focus:ring-ui-ring/40 focus:border-ui-ring';

// ── Component ─────────────────────────────────────────────────────────────────

const InvitationAcceptPage: React.FC = () => {
  const { t } = useTranslation();
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: invData, isLoading: invLoading, isError } = useGetInvitationByTokenQuery(token, { skip: !token });
  const { data: authConfigData } = useGetAuthConfigQuery();
  const [acceptInvitation, { isLoading }] = useAcceptInvitationMutation();

  const enabledMethods = authConfigData?.data?.methods ?? ['native'];
  const requirePassword = enabledMethods.includes('native');
  const showGoogle = enabledMethods.includes('google');

  const inv = invData?.data ?? {};

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showCf, setShowCf] = useState(false);
  const [error, setError] = useState('');

  // Shared: stamp session + navigate after a successful accept (native or Google).
  const finishAccept = (result: unknown) => {
    const data = (result as { data: TokenData }).data;
    if (data?.access_token) {
      dispatch(clearSession());
      dispatch(setSession({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        user: {
          user_id: data.user_id,
          email: data.email,
          name: data.name || (inv.invitee_name as string | undefined) || '',
          roles: data.roles,
          role_names: data.role_names,
          permissions: data.permissions,
          tenant_id: data.tenant_id ?? '',
          subscription_status: data.subscription_status,
          onboarding_completed: data.onboarding_completed,
          sam_welcome_shown: data.sam_welcome_shown,
        },
      }));
      navigate('/');
    } else {
      navigate('/login?accepted=1');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (requirePassword) {
      if (password.length < 8) {
        setError(t('auth.passwordMinLength'));
        return;
      }
      if (password !== confirm) {
        setError(t('auth.passwordsDoNotMatch'));
        return;
      }
    }

    try {
      const preFilled = String(inv.invitee_name ?? inv.name ?? '');
      const result = await acceptInvitation({
        token,
        name: preFilled,
        ...(requirePassword ? { password } : {}),
      } as Parameters<typeof acceptInvitation>[0]).unwrap();
      finishAccept(result);
    } catch (err: unknown) {
      const apiErr = err as { data?: { message?: string } };
      setError(getErrorDetail(err) ?? apiErr?.data?.message ?? 'Something went wrong. Please try again.');
    }
  };

  const handleGoogleSuccess = async (credential: string) => {
    setError('');
    try {
      const preFilled = String(inv.invitee_name ?? inv.name ?? '');
      const result = await acceptInvitation({
        token,
        name: preFilled,
        google_id_token: credential,
      } as Parameters<typeof acceptInvitation>[0]).unwrap();
      finishAccept(result);
    } catch (err: unknown) {
      const apiErr = err as { data?: { message?: string } };
      setError(getErrorDetail(err) ?? apiErr?.data?.message ?? 'Google sign-in failed. Please try again.');
    }
  };

  const invStatus = invData?.data?.status as string | undefined;
  const isExpired  = invStatus === 'Expired'  || (isError && false);
  const isAccepted = invStatus === 'Accepted';
  const isRevoked  = invStatus === 'Revoked';

  const goToSignIn = () => {
    dispatch(clearSession());
    navigate('/login');
  };

  const invitationError: { title: string; body: string } | null =
    !token
      ? { title: 'Invitation link unavailable', body: 'This invitation link is invalid, expired, or has already been used. Please ask your company administrator or the person who invited you to send a new invitation.' }
      : isError
      ? { title: 'Invitation link unavailable', body: 'This invitation link is invalid, expired, or has already been used. Please ask your company administrator or the person who invited you to send a new invitation.' }
      : isExpired
      ? { title: 'This invitation has expired', body: 'Ask your company administrator or the person who invited you to send a new invitation.' }
      : isAccepted
      ? { title: 'Invitation already accepted', body: 'Your account may already be active. Sign in to continue.' }
      : isRevoked
      ? { title: 'Invitation link unavailable', body: 'This invitation has been revoked. Please ask your company administrator to send a new invitation.' }
      : null;

  // ── Loading / error states ───────────────────────────────────────────────────

  if (invLoading) {
    return (
      <div className="auth-bg flex items-center justify-center min-h-dvh relative overflow-hidden">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-ui-text2">Loading your invitation…</p>
        </div>
      </div>
    );
  }

  if (invitationError) {
    return (
      <div className="auth-bg flex items-center justify-center min-h-dvh relative overflow-hidden">
        <div className="card-surface p-10 text-center max-w-sm w-full">
          <div className="w-14 h-14 rounded-full bg-danger/10 flex items-center justify-center mx-auto mb-4">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6 text-danger-text">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h2 className="text-h3 text-ui-text mb-2">{invitationError.title}</h2>
          <p className="text-sm text-ui-text2 mb-6">{invitationError.body}</p>
          <Button size="lg" className="w-full"
            onClick={goToSignIn}>
            Go to sign in
          </Button>
        </div>
      </div>
    );
  }

  // ── Main wizard layout ───────────────────────────────────────────────────────

  return (
    <div className="auth-bg h-dvh overflow-hidden flex relative">
      {/* Left branded panel — hidden on mobile, visible md+ */}
      <div className="hidden md:block h-dvh shrink-0">
        <LeftSidePanel className="h-full">
          <OnboardingStepNav
            heading="Welcome aboard!"
            subtitle="Complete these steps to activate your account."
            steps={NAV_STEPS}
            activeId="password"
          />
        </LeftSidePanel>
      </div>

      {/* Right content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <div className="flex-1 overflow-y-auto no-scrollbar">
          <div className="min-h-full flex items-center justify-center p-4 sm:p-6 lg:p-8">
          <div className="w-full max-w-lg mx-auto">
            <div className="bg-ui-card rounded-2xl p-8 shadow-sm">


              {/* Header */}
              <h2 className="text-h3 text-ui-text mb-1">Activate your account</h2>
              <p className="text-ui-text2 text-sm mb-6">
                {inv.invitee_name ? `Welcome, ${inv.invitee_name}! ` : ''}
                {showGoogle && requirePassword
                  ? 'Continue with Google or create a password to join your team.'
                  : showGoogle
                  ? 'Continue with Google to join your team.'
                  : 'Create a password to complete your account setup and join your team.'}
              </p>

              {/* ── Google Sign-In (primary, shown first when enabled) ── */}
              {showGoogle && (
                // [color-scheme:light] keeps the light-scheme GIS iframe transparent in dark mode
                // (see LoginPage) instead of showing a white box behind it.
                <div className="flex justify-center mb-2 [color-scheme:light]">
                  <GoogleLogin
                    onSuccess={(cr) => { if (cr.credential) handleGoogleSuccess(cr.credential); }}
                    onError={() => setError('Google sign-in failed. Please try again.')}
                    width="368"
                    text="continue_with"
                    shape="rectangular"
                  />
                </div>
              )}

              {/* ── Divider: only shown when both methods are available ── */}
              {showGoogle && requirePassword && (
                <div className="flex items-center gap-3 my-5">
                  <div className="flex-1 h-px bg-ui-border" />
                  <span className="text-xs text-ui-text3 font-medium">or create a password</span>
                  <div className="flex-1 h-px bg-ui-border" />
                </div>
              )}

              {/* ── Native password form ── */}
              {requirePassword && (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-ui-text mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPw ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Min. 8 characters"
                        required
                        autoComplete="new-password"
                        className={inputCls + ' pr-10'}
                      />
                      <button
                        type="button"
                        onMouseDown={() => setShowPw(true)}
                        onMouseUp={() => setShowPw(false)}
                        onMouseLeave={() => setShowPw(false)}
                        onTouchStart={() => setShowPw(true)}
                        onTouchEnd={() => setShowPw(false)}
                        className="absolute inset-y-0 right-3 flex items-center text-ui-text3 hover:text-ui-text2"
                        tabIndex={-1}
                        aria-label="Hold to show password"
                      >
                        <EyeIcon off={showPw} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ui-text mb-1.5">
                      Confirm password
                    </label>
                    <div className="relative">
                      <input
                        type={showCf ? 'text' : 'password'}
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        placeholder="Re-enter your password"
                        required
                        autoComplete="new-password"
                        className={inputCls + ' pr-10'}
                      />
                      <button
                        type="button"
                        onMouseDown={() => setShowCf(true)}
                        onMouseUp={() => setShowCf(false)}
                        onMouseLeave={() => setShowCf(false)}
                        onTouchStart={() => setShowCf(true)}
                        onTouchEnd={() => setShowCf(false)}
                        className="absolute inset-y-0 right-3 flex items-center text-ui-text3 hover:text-ui-text2"
                        tabIndex={-1}
                        aria-label="Hold to show password"
                      >
                        <EyeIcon off={showCf} />
                      </button>
                    </div>
                    {confirm && password && confirm !== password && (
                      <p className="text-xs text-danger-text mt-1.5">Passwords do not match.</p>
                    )}
                  </div>

                  {error && (
                    <div className="bg-danger/10 border border-danger/30 rounded-lg px-4 py-3 text-sm text-danger-text">
                      {error}
                    </div>
                  )}

                  <Button size="lg" className="w-full mt-2"
                    type="submit"
                    disabled={isLoading || (!password || !confirm)}>
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Setting up your account…
                      </>
                    ) : (
                      <>
                        Continue
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                          strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </Button>
                </form>
              )}

              {/* Error shown outside the form — for Google-only tenants */}
              {!requirePassword && error && (
                <div className="mt-4 bg-danger/10 border border-danger/30 rounded-lg px-4 py-3 text-sm text-danger-text">
                  {error}
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvitationAcceptPage;
